import React, { useEffect, useState, useRef } from 'react';

export default function VehiclePacket() {

  const pathParts = window.location.pathname.split('/');
  const dealershipId = pathParts[2];
  const vin = pathParts[3];
  const token = pathParts[4];

  const [vehicle, setVehicle] = useState(null);
  const [dealership, setDealership] = useState(null);
  const [loading, setLoading] = useState(true);

  // Calculator state
  const [paymentType, setPaymentType] = useState('finance');
  const [calcDownPayment, setCalcDownPayment] = useState('');
  const [calcTerm, setCalcTerm] = useState(60);
  const [calcCreditScore, setCalcCreditScore] = useState(700);
  const [calcApr, setCalcApr] = useState(5.99);
  const [includeTaxes, setIncludeTaxes] = useState(false);
  const [showTradeIn, setShowTradeIn] = useState(false);
  const [tradeInValue, setTradeInValue] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [taxRegion, setTaxRegion] = useState('');
  const [taxState, setTaxState] = useState('');
  const [taxLoading, setTaxLoading] = useState(false);
  const [logId, setLogId] = useState(null);

  // Add state for showing the customer info form
  const [showCustomerForm, setShowCustomerForm] = useState(false);

  // Add state for customer form fields and submission status
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerZip, setCustomerZip] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  // Add state for PDF URLs first
  const [inspectionPdfUrl, setInspectionPdfUrl] = useState('');
  const [autoCheckPdfUrl, setAutoCheckPdfUrl] = useState('');
  const [msrpPdfUrl, setMsrpPdfUrl] = useState('');

  // Dynamic navigation links based on available PDFs
  const navLinks = [
    { label: 'General Info.' },
    ...(autoCheckPdfUrl ? [{ label: 'AutoCheck History' }] : []),
    ...(inspectionPdfUrl ? [{ label: 'Inspection Forms' }] : []),
    ...(msrpPdfUrl ? [{ label: 'Original MSRP / Options' }] : []),
  ];
  
  const [activeTab, setActiveTab] = useState('General Info.');
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showAutoCheckModal, setShowAutoCheckModal] = useState(false);
  const [showMsrpModal, setShowMsrpModal] = useState(false);
  const accentHeight = 6;

  // Add state for seller video URL
  const [videoUrl, setVideoUrl] = useState('');

  // Add state for showing trade-in inputs and trade payoff
  const [hasTradePayoff, setHasTradePayoff] = useState(false);
  const [tradePayoff, setTradePayoff] = useState('');

  // Add state for modal photo viewer
  const [photoModal, setPhotoModal] = useState({ open: false, index: 0 });

  // Add state for submit status
  const [submitStatus, setSubmitStatus] = useState('');

  // Add state for customer logs
  const [customerLogs, setCustomerLogs] = useState([]);

  // Add state for redirecting
  const [redirecting, setRedirecting] = useState(false);

  // Add state for packet template
  const [packetTemplate, setPacketTemplate] = useState('default');

  // Update payment type when template changes
  useEffect(() => {
    if (packetTemplate === 'cash-only') {
      setPaymentType('cash');
    }
  }, [packetTemplate]);

  // Animation refs for space background
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const starsRef = useRef([]);

  // State management



  // Track which videos have been clicked to avoid re-tracking
  const [clickedVideos, setClickedVideos] = useState(new Set());

  useEffect(() => {
    // Fetch both vehicle and dealership data
    Promise.all([
      fetch(`/api/dealerships/${dealershipId}/vehicle/${vin}`).then(res => res.json()),
      fetch(`/api/dealerships/${dealershipId}`).then(res => res.json())
    ])
      .then(([vehicleData, dealershipData]) => {
        setVehicle(vehicleData);
        setDealership(dealershipData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching data:', error);
        setLoading(false);
      });
  }, [dealershipId, vin]);

  // Mark packet log as opened
  useEffect(() => {
    if (!dealershipId || !vin || !token) return;
    // The link format used in logs
    const link = `/vehicle-packet/${dealershipId}/${vin}/${token}`;
    
    // Call backend to handle opening (will create new session if previous was closed)
    fetch(`/api/dealerships/api/packet-logs/handle-open`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        dealershipId,
        link,
        vin
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.logId) {
          setLogId(data.logId);
          // Fetch the packet log to get template information
          fetch(`/api/dealerships/api/packet-logs/${data.logId}`)
            .then(res => res.json())
            .then(logData => {
              if (logData && logData.template) {
                setPacketTemplate(logData.template);
              }
            })
            .catch(error => {
              console.error('Error fetching packet log:', error);
            });
        } else {
          setLoading(false);
        }
      })
      .catch(error => {
        setLoading(false);
      });
  }, [dealershipId, vin, token]);

  useEffect(() => {
    if (!logId) return;
    
    const interval = setInterval(() => {
      fetch(`/api/dealerships/api/packet-logs/${logId}/heartbeat`, { method: 'POST' });
    }, 15000); // every 15 seconds
    
    // Detect if user is on mobile device
    const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Handle actual tab/window close events (Windows: only close trigger, Mobile: fallback)
    const handleUnload = () => {
      if (logId) {
        console.log('🔍 Tab/window closing - closing session');
        // Try fetch first with keepalive, then fallback to sendBeacon
        try {
          fetch(`/api/dealerships/api/packet-logs/${logId}/close`, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'page_unload' })
          });
        } catch (e) {
          if (navigator.sendBeacon) {
            const blob = new Blob([JSON.stringify({ action: 'beacon_close' })], { type: 'application/json' });
            navigator.sendBeacon(`/api/dealerships/api/packet-logs/${logId}/close`, blob);
          }
        }
      }
    };

    // Handle visibility change (mobile: delay to avoid false closes when switching apps)
    let visibilityTimeout = null;
    const handleVisibilityChange = () => {
      if (logId && isMobile) {
        if (document.visibilityState === 'hidden') {
          // Mobile: wait 30 seconds before closing to see if user comes back
          console.log('🔍 Page visibility changed to hidden - waiting 30s before closing session (mobile)');
          visibilityTimeout = setTimeout(() => {
            if (document.visibilityState === 'hidden') {
              console.log('🔍 Page still hidden after 30s - closing session (mobile)');
              try {
                fetch(`/api/dealerships/api/packet-logs/${logId}/close`, {
                  method: 'POST',
                  keepalive: true,
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ action: 'visibility_hidden_delayed' })
                });
              } catch (e) {
                console.log('🔍 Fetch failed, using sendBeacon for visibility close');
                if (navigator.sendBeacon) {
                  const blob = new Blob([JSON.stringify({ action: 'beacon_visibility_close' })], { type: 'application/json' });
                  navigator.sendBeacon(`/api/dealerships/api/packet-logs/${logId}/close`, blob);
                }
              }
            }
          }, 30000); // 30 seconds
        } else if (document.visibilityState === 'visible') {
          // Page became visible again - cancel any pending close
          if (visibilityTimeout) {
            console.log('🔍 Page became visible again - canceling pending close (mobile)');
            clearTimeout(visibilityTimeout);
            visibilityTimeout = null;
          }
        }
      }
    };

    // Handle pagehide event (iOS Safari specific)
    const handlePageHide = () => {
      if (logId) {
        console.log('🔍 Page hide event fired - closing session (iOS Safari)');
        // Use sendBeacon for pagehide as it's more reliable
        if (navigator.sendBeacon) {
          const blob = new Blob([JSON.stringify({ action: 'page_hide' })], { type: 'application/json' });
          navigator.sendBeacon(`/api/dealerships/api/packet-logs/${logId}/close`, blob);
        }
      }
    };

    // Handle focus loss (mobile support only - more conservative to avoid false positives)
    let focusTimeout = null;
    const handleFocusOut = () => {
      if (logId && isMobile) {
        console.log('🔍 Window focus lost - waiting before potential close (mobile)');
        // Wait 10 seconds to see if user comes back
        focusTimeout = setTimeout(() => {
          if (document.visibilityState === 'hidden') {
            console.log('🔍 Focus lost and page hidden for 10s - closing session (mobile)');
            try {
              fetch(`/api/dealerships/api/packet-logs/${logId}/close`, {
                method: 'POST',
                keepalive: true,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'focus_lost_delayed' })
              });
            } catch (e) {
              if (navigator.sendBeacon) {
                const blob = new Blob([JSON.stringify({ action: 'beacon_focus_lost' })], { type: 'application/json' });
                navigator.sendBeacon(`/api/dealerships/api/packet-logs/${logId}/close`, blob);
              }
            }
          }
        }, 10000); // 10 seconds
      }
    };

    // Handle focus return (cancel pending close)
    const handleFocusIn = () => {
      if (focusTimeout) {
        console.log('🔍 Window focus returned - canceling pending close (mobile)');
        clearTimeout(focusTimeout);
        focusTimeout = null;
      }
    };
    
    // Add event listeners
    window.addEventListener('beforeunload', handleUnload); // All devices: actual tab/window close
    document.addEventListener('visibilitychange', handleVisibilityChange); // Mobile only (handled in function)
    
    // Mobile-specific events
    if (isMobile) {
      window.addEventListener('pagehide', handlePageHide);
      window.addEventListener('blur', handleFocusOut);
      window.addEventListener('focus', handleFocusIn);
    }
    
    return () => {
      clearInterval(interval);
      // Clear any pending timeouts
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
      if (focusTimeout) {
        clearTimeout(focusTimeout);
      }
      
      window.removeEventListener('beforeunload', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (isMobile) {
        window.removeEventListener('pagehide', handlePageHide);
        window.removeEventListener('blur', handleFocusOut);
        window.removeEventListener('focus', handleFocusIn);
      }
    };
  }, [logId]);

  useEffect(() => {
    if (!dealershipId || !vin) return;
    fetch(`/api/dealerships/${dealershipId}/settings`)
      .then(res => res.json())
      .then(settings => {
        if (settings && settings.vehicleVideos) {
          // Try all case variants
          const direct = settings.vehicleVideos[vin];
          const upper = settings.vehicleVideos[vin?.toUpperCase()];
          const lower = settings.vehicleVideos[vin?.toLowerCase()];
          setVideoUrl(direct || upper || lower || '');
        } else {
          setVideoUrl('');
        }
        // Inspection PDF
        if (settings && settings.inspectionPdfs) {
          const vinSafe = vin.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          const pdfUrl = settings.inspectionPdfs[vinSafe] || '';
          setInspectionPdfUrl(pdfUrl);
        } else {
          setInspectionPdfUrl('');
        }
        // AutoCheck PDF
        if (settings && settings.autoCheckPdfs) {
          const vinSafe = vin.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          setAutoCheckPdfUrl(settings.autoCheckPdfs[vinSafe] || '');
        } else {
          setAutoCheckPdfUrl('');
        }
        // MSRP PDF
        const vinSafe = vin.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        setMsrpPdfUrl(settings.msrpPdfs[vinSafe] || '');
      });
  }, [dealershipId, vin]);

  // Track photo count when vehicle loads
  useEffect(() => {
    if (!vehicle || !logId) return;
    
    const imageOne = (vehicle.imageOne || vehicle['Image One'] || vehicle.ImageOne || vehicle.image1 || vehicle.Image1 || '');
    const imageUrls = (vehicle.imageUrls || vehicle['imageUrls'] || vehicle.ImageUrls || '');
    const gallery = imageUrls.split(',').map(url => url.trim()).filter(url => url && (url.startsWith('http') || url.startsWith('/')));
    
    const photoCount = gallery.length + (imageOne ? 1 : 0);
  }, [vehicle, logId]);

  // Initialize stars
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Create stars
    const stars = [];
    for (let i = 0; i < 150; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 1.5 + 0.3,
        speed: Math.random() * 0.3 + 0.05,
        opacity: Math.random() * 0.6 + 0.2,
        twinkle: Math.random() * 0.02 + 0.005
      });
    }
    starsRef.current = stars;
    
    // Handle resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mouse tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const stars = starsRef.current;
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw moving stars
      stars.forEach(star => {
        // Move stars
        star.y += star.speed;
        if (star.y > canvas.height) {
          star.y = -5;
          star.x = Math.random() * canvas.width;
        }
        
        // Twinkle effect
        star.opacity += star.twinkle;
        if (star.opacity > 1 || star.opacity < 0.1) {
          star.twinkle = -star.twinkle;
        }
        
        // Mouse interaction - subtle attraction to mouse
        const dx = mouseRef.current.x - star.x;
        const dy = mouseRef.current.y - star.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          const force = (100 - distance) / 100;
          star.x += dx * force * 0.005;
          star.y += dy * force * 0.005;
        }
        
        // Draw star
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.fill();
        
        // Add subtle glow effect
        if (star.size > 1) {
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.size * 1.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(135, 206, 250, ${star.opacity * 0.2})`;
          ctx.fill();
        }
      });
      
      // Draw subtle nebula effect around mouse
      const gradient = ctx.createRadialGradient(
        mouseRef.current.x, mouseRef.current.y, 0,
        mouseRef.current.x, mouseRef.current.y, 150
      );
      gradient.addColorStop(0, 'rgba(138, 43, 226, 0.03)');
      gradient.addColorStop(0.5, 'rgba(75, 0, 130, 0.02)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  function getAprForCreditScore(score, type) {
    if (type === 'cash') return 0;
    if (score >= 750) return 3.99;
    if (score >= 700) return 5.99;
    if (score >= 650) return 7.99;
    return 9.99;
  }

  const lookupTaxRate = async (zip) => {
    if (!zip || zip.length !== 5) {
      setTaxRate(0);
      setTaxRegion('');
      setTaxState('');
      return;
    }
    setTaxLoading(true);
    try {
      const response = await fetch(`/api/dealerships/utils/tax-rate/${zip}`);
      const data = await response.json();
      if (response.ok) {
        setTaxRate(data.rate * 100); // Convert to percentage
        setTaxRegion(data.regionName);
        setTaxState(data.state);
      } else {
        setTaxRate(0);
        setTaxRegion('');
        setTaxState('');
      }
    } catch (error) {
      setTaxRate(0);
      setTaxRegion('');
      setTaxState('');
    }
    setTaxLoading(false);
  };

  const handleZipCodeChangeLogged = (e) => {
    const newValue = e.target.value.replace(/\D/g, '').slice(0, 5);
    logInputChange('zipCode', zipCode, newValue);
    setZipCode(newValue);
    if (newValue.length === 5) {
      lookupTaxRate(newValue);
    } else {
      setTaxRate(0);
      setTaxRegion('');
      setTaxState('');
    }
  };

  useEffect(() => {
    if (zipCode.length === 5) {
      setTaxLoading(true);
      fetch(`/api/taxrate/${zipCode}`)
        .then(res => res.json())
        .then(data => {
          if (data && data.taxRate) {
            setTaxRate(data.taxRate); // auto-set the recommended tax rate
          }
          setTaxLoading(false);
        })
        .catch(() => setTaxLoading(false));
    }
  }, [zipCode]);

  // Add this effect to auto-update APR when credit score or payment type changes
  useEffect(() => {
    setCalcApr(getAprForCreditScore(calcCreditScore, paymentType));
  }, [calcCreditScore, paymentType]);

  function logInputChange(field, oldValue, newValue) {
    const logEntry = {
      field,
      oldValue,
      newValue,
      timestamp: Date.now(),
    };
    setCustomerLogs(logs => [...logs, logEntry]);

    // Send to backend immediately if logId is available
    if (logId) {
      console.log('logId:', logId, 'logEntry:', logEntry);
      fetch(`/api/dealerships/api/packet-logs/${logId}/options`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerLogs: [logEntry] }),
      });
    }
  }

  // Simple tracking function for click stream
  function trackInteraction(type, data) {
    console.log('🔍 trackInteraction called:', { type, data, logId });
    
    if (!logId) {
      console.log('❌ No logId found, cannot track interaction');
      return;
    }
    
    const logEntry = {
      field: 'user_interaction',
      oldValue: type,
      newValue: JSON.stringify(data),
      timestamp: Date.now(),
    };
    
    console.log('📝 Created log entry:', logEntry);
    
    setCustomerLogs(logs => [...logs, logEntry]);
    
    console.log('📡 Sending track interaction to API...');
    
    fetch(`/api/dealerships/api/packet-logs/${logId}/options`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerLogs: [logEntry] }),
    })
    .then(response => {
      console.log('✅ Track interaction response:', response.status, response.ok);
      if (!response.ok) {
        console.log('❌ Track interaction failed:', response.status);
      }
      return response.json();
    })
    .then(data => {
      console.log('📊 Track interaction response data:', data);
    })
    .catch(error => {
      console.log('🚨 Track interaction error:', error);
    });
  }

  // Example for calcDownPayment (repeat for all fields):
  const handleDownPaymentChange = (e) => {
    const inputValue = e.target.value;
    const newValue = inputValue === '' ? '' : Number(inputValue);
    logInputChange('calcDownPayment', calcDownPayment, newValue);
    setCalcDownPayment(newValue);
  };

  // For calcTerm
  const handleTermChange = (newValue) => {
    logInputChange('calcTerm', calcTerm, newValue);
    setCalcTerm(newValue);
  };

  // For calcCreditScore
  const handleCreditScoreChange = (e) => {
    const newValue = Number(e.target.value);
    logInputChange('calcCreditScore', calcCreditScore, newValue);
    setCalcCreditScore(newValue);
  };

  // For calcApr
  const handleAprChange = (e) => {
    const newValue = Number(e.target.value);
    logInputChange('calcApr', calcApr, newValue);
    setCalcApr(newValue);
  };

  // For tradeInValue
  const handleTradeInValueChange = (e) => {
    const newValue = e.target.value;
    logInputChange('tradeInValue', tradeInValue, newValue);
    setTradeInValue(newValue);
  };

  // For tradePayoff
  const handleTradePayoffChange = (e) => {
    const newValue = e.target.value;
    logInputChange('tradePayoff', tradePayoff, newValue);
    setTradePayoff(newValue);
  };

  // For paymentType
  const handlePaymentTypeChange = (type) => {
    logInputChange('paymentType', paymentType, type);
    setPaymentType(type);
  };

  // For includeTaxes
  const handleIncludeTaxesChange = (checked) => {
    logInputChange('includeTaxes', includeTaxes, checked);
    setIncludeTaxes(checked);
  };

  // For showTradeIn
  const handleShowTradeInChange = (checked) => {
    logInputChange('showTradeIn', showTradeIn, checked);
    setShowTradeIn(checked);
  };

  // For hasTradePayoff
  const handleHasTradePayoffChange = (checked) => {
    logInputChange('hasTradePayoff', hasTradePayoff, checked);
    setHasTradePayoff(checked);
  };

  // Add this function to handle submit
  async function handleSubmitOptions() {
    if (!logId) return;
    setSubmitStatus('submitting');
    // Calculate monthly payment
    let monthlyPayment = null;
    const price = vehicle?.internetPrice || vehicle?.price || vehicle?.retail || vehicle?.cost || vehicle?.wholesalePrice || 
                  vehicle?.Price || vehicle?.msrp || vehicle?.MSRP || vehicle?.InternetPrice || vehicle?.Retail || vehicle?.Cost ||
                  vehicle?.['Internet Price'] || vehicle?.['Retail'] || vehicle?.['Cost'] || vehicle?.['MSRP'] || vehicle?.['Price'];
    const priceNum = Number(price) || 0;
    if (priceNum > 0 && calcTerm && calcApr !== undefined) {
      const down = Number(calcDownPayment) || 0;
      const trade = showTradeIn ? (Number(tradeInValue) || 0) : 0;
      const payoff = hasTradePayoff ? (Number(tradePayoff) || 0) : 0;
      const principal = priceNum - down - trade + payoff;
      const apr = Number(calcApr) / 100;
      const n = Number(calcTerm);
      if (principal > 0 && n > 0) {
        const r = apr / 12;
        monthlyPayment = r > 0
          ? (principal * r) / (1 - Math.pow(1 + r, -n))
          : principal / n;
      }
    }
    const options = {
      paymentType,
      calcDownPayment,
      calcTerm,
      calcCreditScore,
      calcApr,
      includeTaxes,
      showTradeIn,
      tradeInValue,
      hasTradePayoff,
      tradePayoff,
      zipCode,
      taxRate,
      taxRegion,
      taxState,
      monthlyPayment,
    };
    try {
      console.log('Submitting options for logId:', logId, 'link:', window.location.href, options, customerLogs);
      const res = await fetch(`/api/dealerships/api/packet-logs/${logId}/options`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ options, customerLogs }),
      });
      if (res.ok) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      setSubmitStatus('error');
    }
  }

  // Add handlers for customer info fields
  const handleCustomerFirstNameChange = (e) => {
    logInputChange('customerFirstName', customerFirstName, e.target.value);
    setCustomerFirstName(e.target.value);
  };
  const handleCustomerLastNameChange = (e) => {
    logInputChange('customerLastName', customerLastName, e.target.value);
    setCustomerLastName(e.target.value);
  };
  const handleCustomerEmailChange = (e) => {
    logInputChange('customerEmail', customerEmail, e.target.value);
    setCustomerEmail(e.target.value);
  };
  const handleCustomerPhoneChange = (e) => {
    logInputChange('customerPhone', customerPhone, e.target.value);
    setCustomerPhone(e.target.value);
  };
  const handleCustomerZipChange = (e) => {
    logInputChange('customerZip', customerZip, e.target.value);
    setCustomerZip(e.target.value);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-xl">Loading...</div>;
  


  if (!vehicle) return <div className="min-h-screen flex items-center justify-center text-xl">Vehicle not found.</div>;

  const year = vehicle.year || vehicle.Year || '';
  const make = vehicle.make || vehicle.Make || '';
  const model = vehicle.model || vehicle.Model || '';
  const trim = vehicle.trim || vehicle.Trim || '';
  
  // Check for price in multiple possible field names from CSV
  let price = vehicle.internetPrice || vehicle.price || vehicle.retail || vehicle.cost || vehicle.wholesalePrice || 
              vehicle.Price || vehicle.msrp || vehicle.MSRP || vehicle.InternetPrice || vehicle.Retail || vehicle.Cost ||
              vehicle['Internet Price'] || vehicle['Retail'] || vehicle['Cost'] || vehicle['MSRP'] || vehicle['Price'] || '';
  if (typeof price === 'string') {
    price = price.replace(/[^0-9.]/g, '');
  }
  if (!price || isNaN(Number(price)) || Number(price) <= 0) {
    price = 'Call for Price';
  } else {
    price = Number(price);
  }
  
  const mileageRaw = vehicle.mileage || vehicle.Mileage || vehicle.miles || vehicle.Miles || '';
  const mileage = mileageRaw ? Number(mileageRaw).toLocaleString() : 'N/A';
  const exteriorColor = vehicle.exteriorColor || vehicle['Exterior Color'] || vehicle.ExteriorColor || 'N/A';
  const interiorColor = vehicle.interiorColor || vehicle['Interior Color'] || vehicle.InteriorColor || 'N/A';
  const body = vehicle.body || vehicle.bodyType || vehicle.Body || vehicle.BodyType || 'N/A';
  const drivetrain = vehicle.drivetrain || vehicle.drive || vehicle.Drivetrain || vehicle.Drive || 'N/A';
  const stockNumber = vehicle.stockNumber || vehicle.stocknumber || vehicle['Stock Number'] || vehicle.StockNumber || 'N/A';
  const vinField = vehicle.vin || vehicle.VIN || 'N/A';
  const imageOne = (vehicle.imageOne || vehicle['Image One'] || vehicle.ImageOne || vehicle.image1 || vehicle.Image1 || '');
  const imageUrls = (vehicle.imageUrls || vehicle['imageUrls'] || vehicle.ImageUrls || '');
  const gallery = imageUrls.split(',').map(url => url.trim()).filter(url => url && (url.startsWith('http') || url.startsWith('/'))); 
  const mainImageUrl = imageOne && (imageOne.startsWith('http') || imageOne.startsWith('/')) ? imageOne : '';
  const mainImage = mainImageUrl || gallery[0] || 'https://via.placeholder.com/600x350?text=No+Image';
  const description = vehicle.description || 'No description available.';

  // Calculator logic
  const calcVehiclePrice = typeof price === 'number' ? price : 0;
  const fallbackTaxRate = 7;
  const effectiveTaxRate = (includeTaxes && (taxRate > 0 ? taxRate : fallbackTaxRate)) || 0;
  // Calculate net trade-in credit
  const netTradeInCredit = showTradeIn && tradeInValue
    ? Math.max(Number(tradeInValue) - (hasTradePayoff && tradePayoff ? Number(tradePayoff) : 0), 0)
    : 0;
  const hasTradeIn = showTradeIn && netTradeInCredit > 0;
  const taxableAmount = hasTradeIn ? Math.max(calcVehiclePrice - netTradeInCredit, 0) : calcVehiclePrice;
  const calcTaxes = includeTaxes ? Math.round(taxableAmount * (effectiveTaxRate / 100)) : 0;
  const calcFees = 245.28;
  const calcMonthlyPayment =
    paymentType === 'finance' && calcVehiclePrice
      ? (
          (((calcVehiclePrice + calcTaxes + calcFees - calcDownPayment - netTradeInCredit) * (calcApr / 100 / 12) *
            Math.pow(1 + calcApr / 100 / 12, calcTerm)) /
          (Math.pow(1 + calcApr / 100 / 12, calcTerm) - 1))
        ) || 0
      : 0;

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Space Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{
          background: 'linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #533483 100%)'
        }}
      />
      
      {/* Floating particles overlay */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-purple-400 rounded-full animate-ping"></div>
        <div className="absolute top-3/4 right-1/4 w-0.5 h-0.5 bg-blue-400 rounded-full animate-pulse"></div>
        <div className="absolute top-1/2 left-1/6 w-1 h-1 bg-cyan-400 rounded-full animate-bounce"></div>
        <div className="absolute top-1/6 right-1/3 w-0.5 h-0.5 bg-purple-300 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-1/4 left-1/3 w-1 h-1 bg-indigo-400 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center p-0">
      {/* Nav Bar + Accent Line (matches admin style) */}
      <div className="w-full flex flex-col items-center">
        <div
          className="w-[95vw] max-w-6xl flex flex-col sm:flex-row items-center px-4 sm:px-8 py-4 gap-2 sm:gap-4 animate-slideDown"
          style={{
            background: 'rgba(255,255,255,0.7)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            borderRadius: '2rem',
            border: '1.5px solid rgba(255,255,255,0.25)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            minHeight: 64,
            marginTop: 12,
            position: 'relative',
          }}
        >
          {/* Mobile Card-Based Navigation */}
          <div className="w-full flex sm:hidden justify-center mb-2">
            <div className="w-full max-w-xs space-y-2">
              {navLinks.map(link => {
                const isActive = activeTab === link.label;
                const isPdf = ['Inspection Forms', 'AutoCheck History', 'Original MSRP / Options'].includes(link.label);
                
                return (
                  <button
                    key={link.label}
                    className={`
                      w-full p-3 rounded-xl font-semibold text-left transition-all duration-200 
                      ${isActive && !isPdf 
                        ? 'bg-blue-500 text-white shadow-md' 
                        : 'bg-white/90 text-gray-800 border border-gray-200 hover:border-blue-300'
                      }
                      backdrop-blur-sm text-sm
                    `}
                    onClick={() => {
                      if (link.label === 'Inspection Forms') {
                        setShowInspectionModal(true);
                        trackInteraction('pdf_clicked', { type: 'inspection', platform: 'mobile', vin: vehicle.vin || vin });
                      } else if (link.label === 'AutoCheck History') {
                        setShowAutoCheckModal(true);
                        trackInteraction('pdf_clicked', { type: 'autocheck', platform: 'mobile', vin: vehicle.vin || vin });
                      } else if (link.label === 'Original MSRP / Options') {
                        setShowMsrpModal(true);
                        trackInteraction('pdf_clicked', { type: 'msrp', platform: 'mobile', vin: vehicle.vin || vin });
                      } else {
                        setActiveTab(link.label);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-lg mr-2">
                          {link.label === 'General Info.'}
                          {link.label === 'AutoCheck History'}
                          {link.label === 'Inspection Forms'}
                          {link.label === 'Original MSRP / Options'}
                        </span>
                        <span>{link.label}</span>
                      </div>
                      <span className="text-lg">
                        {isPdf ? '📄' : '→'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Desktop Tab Nav */}
          <div className="vehicle-detail-main-nav overflow-x-auto whitespace-nowrap flex flex-row gap-1 sm:gap-2 relative w-full hidden sm:flex" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* Right gradient scroll hint for mobile only */}
            <div className="absolute right-0 top-0 h-full w-8 pointer-events-none block sm:hidden" style={{background: 'linear-gradient(to left, #fff 60%, transparent)'}}></div>
            {navLinks.map((link, idx) => {
              if (link.label === 'Inspection Forms') {
                return (
                  <button
                    key={link.label}
                    className={`text-xs sm:text-base font-semibold px-2 sm:px-5 py-1.5 sm:py-2 rounded-full transition duration-200 ${activeTab === link.label ? 'bg-blue-500/90 text-white shadow' : 'bg-white/60 text-blue-900 hover:bg-blue-100 hover:text-blue-800'} hover:scale-105 hover:shadow-lg border border-blue-100`}
                    style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.01em' }}
                                            onClick={() => {
                      setShowInspectionModal(true);
                      trackInteraction('pdf_clicked', { type: 'inspection', vin: vehicle.vin || vin });
                    }}
                  >
                    {link.label}
                  </button>
                );
              } else if (link.label === 'AutoCheck History') {
                return (
                  <button
                    key={link.label}
                    className={`text-xs sm:text-base font-semibold px-2 sm:px-5 py-1.5 sm:py-2 rounded-full transition duration-200 ${activeTab === link.label ? 'bg-blue-500/90 text-white shadow' : 'bg-white/60 text-blue-900 hover:bg-blue-100 hover:text-blue-800'} hover:scale-105 hover:shadow-lg border border-blue-100`}
                    style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.01em' }}
                                            onClick={() => {
                      setShowAutoCheckModal(true);
                      trackInteraction('pdf_clicked', { type: 'autocheck', vin: vehicle.vin || vin });
                    }}
                  >
                    {link.label}
                  </button>
                );
              } else if (link.label === 'Original MSRP / Options') {
                return (
                  <button
                    key={link.label}
                    className={`text-xs sm:text-base font-semibold px-2 sm:px-5 py-1.5 sm:py-2 rounded-full transition duration-200 ${activeTab === link.label ? 'bg-blue-500/90 text-white shadow' : 'bg-white/60 text-blue-900 hover:bg-blue-100 hover:text-blue-800'} hover:scale-105 hover:shadow-lg border border-blue-100`}
                    style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.01em' }}
                                            onClick={() => {
                      setShowMsrpModal(true);
                      trackInteraction('pdf_clicked', { type: 'msrp', vin: vehicle.vin || vin });
                    }}
                  >
                    {link.label}
                  </button>
                );
              } else {
                return (
                  <button
                    key={link.label}
                    className={`text-xs sm:text-base font-semibold px-2 sm:px-5 py-1.5 sm:py-2 rounded-full transition duration-200 ${activeTab === link.label ? 'bg-blue-500/90 text-white shadow' : 'bg-white/60 text-blue-900 hover:bg-blue-100 hover:text-blue-800'} hover:scale-105 hover:shadow-lg border border-blue-100`}
                    style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.01em' }}
                    onClick={() => setActiveTab(link.label)}
                  >
                    {link.label}
                  </button>
                );
              }
            })}
          </div>
        </div>
        {/* Accent line */}
        <div className="w-full h-1 bg-gradient-to-r from-blue-400 via-blue-200 to-blue-400 opacity-60" style={{ borderRadius: 8, maxWidth: 1200, height: accentHeight }} />
      </div>
      {/* Enhanced Main Content: Two-column layout */}
      <div className="w-full max-w-7xl bg-white rounded-3xl shadow-2xl flex flex-col lg:flex-row overflow-hidden border-2 border-gray-200 mt-8 mb-8">
        {/* Enhanced Left: Gallery, Video, Action Buttons */}
        <div className="lg:w-1/2 w-full flex flex-col items-center p-6 lg:p-8 bg-gradient-to-br from-gray-50 to-white">
          {/* Modern Main Photo Frame */}
          <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl border-4 border-white overflow-hidden mb-6">
            <div className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <img
                src={mainImage}
                alt={`${year} ${make} ${model}`}
                className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                loading="lazy"
                decoding="async"
                onClick={() => {
                  setPhotoModal({ open: true, index: 0 });
                }}
                onError={e => (e.target.src = 'https://via.placeholder.com/600x350?text=No+Image')}
              />
              {/* Photo Count Badge */}
              <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-semibold">
                📸 {gallery.length || 1} Photos
              </div>
            </div>
          </div>

          {/* Enhanced Gallery Thumbnails */}
          {gallery.length > 1 && (
            <div className="w-full max-w-2xl mb-6">
              <div className="bg-white rounded-2xl p-4 shadow-lg border border-gray-200">
                <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span>🖼️</span>
                  <span>Photo Gallery ({gallery.length} images)</span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {gallery.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`Gallery ${idx + 1}`}
                      className="w-20 h-16 object-cover rounded-xl border-2 border-gray-200 cursor-pointer hover:scale-110 hover:shadow-lg transition-all duration-200 flex-shrink-0"
                      loading="lazy"
                      decoding="async"
                      onClick={() => {
                        setPhotoModal({ open: true, index: idx });
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Seller Videos */}
          {videoUrl && (
            <div className="w-full max-w-2xl mb-6">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-red-500 to-pink-600 text-white p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 rounded-full p-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-5.5-2h3m-3 4h3" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">🎥 Seller Videos</h3>
                      <p className="text-red-100 text-sm">Detailed vehicle walkthrough</p>
                    </div>
                  </div>
                </div>
                                  <div className="aspect-video bg-black flex items-center justify-center relative">
                    {/* Invisible overlay for click tracking - only shows for unclicked videos */}
                    {(videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be') || videoUrl.includes('vimeo.com')) && !clickedVideos.has(videoUrl) && (
                      <div 
                        className="absolute inset-0 z-50 cursor-pointer"
                        onClick={(e) => {
                          console.log('🎥 Video iframe clicked for first time!', { videoUrl });
                          
                          // Track the interaction
                          const platform = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be') ? 'youtube' 
                            : videoUrl.includes('vimeo.com') ? 'vimeo' 
                            : 'direct';
                          
                          console.log('🎥 Tracking video interaction:', { platform, url: videoUrl, vin: vehicle.vin || vin });
                          
                          trackInteraction('video_clicked', { 
                            type: 'seller_video_clicked', 
                            url: videoUrl,
                            platform: platform,
                            vin: vehicle.vin || vin,
                            timestamp: new Date().toISOString()
                          });
                          
                          // Mark this video as clicked and remove overlay after a brief delay
                          setTimeout(() => {
                            setClickedVideos(prev => new Set([...prev, videoUrl]));
                            console.log('🎥 Video marked as clicked, overlay removed - you can now interact with the video normally');
                          }, 200); // Brief delay to ensure tracking completes
                          
                          // Prevent the click from propagating to avoid double events
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                      </div>
                    )}
                    
                    {/* Video iframe */}
                    {videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be') ? (
                      <iframe
                        title="Seller Video"
                        src={
                          videoUrl.includes('watch?v=')
                            ? `https://www.youtube.com/embed/${videoUrl.split('watch?v=')[1].split('&')[0]}`
                            : videoUrl.includes('youtu.be/')
                            ? `https://www.youtube.com/embed/${videoUrl.split('youtu.be/')[1].split('?')[0]}`
                            : videoUrl
                        }
                        className="w-full h-full"
                        allowFullScreen
                      />
                    ) : videoUrl.includes('vimeo.com') ? (
                      <iframe
                        title="Seller Video"
                        src={
                          videoUrl.includes('player.vimeo.com')
                            ? videoUrl
                            : `https://player.vimeo.com/video/${videoUrl.split('vimeo.com/')[1].split(/[?/]/)[0]}`
                        }
                        className="w-full h-full"
                        allowFullScreen
                      />
                    ) : (
                      <video 
                        src={videoUrl} 
                        controls 
                        className="w-full h-full bg-black"
                        onClick={(e) => {
                          console.log('🎥 Direct video clicked!');
                          
                          // Track every click on direct video
                          trackInteraction('video_clicked', { 
                            type: 'seller_video_clicked', 
                            url: videoUrl,
                            platform: 'direct',
                            vin: vehicle.vin || vin,
                            timestamp: new Date().toISOString()
                          });
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

        </div>
        {/* Enhanced Right: Vehicle Info, Calculator, Description */}
        <div className="lg:w-1/2 w-full flex flex-col gap-6 p-6 lg:p-8 bg-gradient-to-bl from-blue-50 to-white">
          {/* Modern Vehicle Info Card */}
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Vehicle Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-6">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 rounded-full p-3">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0V9a1 1 0 011-1h4a1 1 0 011 1v11" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold leading-tight mb-2">
                    {year} {make} {model} {trim}
                  </h1>
                  <div className="flex items-center gap-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Stock: {stockNumber}
                    </span>
                    <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-mono">
                      VIN: {vinField.slice(-6)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Price Section */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 border-b">
              <div className="text-center">
                <div className="text-6xl font-extrabold text-green-600 mb-2 drop-shadow-sm">
                  {typeof price === 'number' ? `$${price.toLocaleString()}` : price}
                </div>
                <div className="text-green-700 font-semibold text-lg">
                  💰 Competitive Pricing Available
                </div>
              </div>
            </div>

            {/* Vehicle Specifications */}
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <span>📋</span>
                  <span>Vehicle Specifications</span>
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="text-gray-600 text-sm font-medium mb-1"> Mileage</div>
                  <div className="font-bold text-gray-900 text-lg">{mileage} miles</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="text-gray-600 text-sm font-medium mb-1"> Exterior</div>
                  <div className="font-bold text-gray-900 text-lg">{exteriorColor}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="text-gray-600 text-sm font-medium mb-1"> Interior</div>
                  <div className="font-bold text-gray-900 text-lg">{interiorColor}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="text-gray-600 text-sm font-medium mb-1"> Drivetrain</div>
                  <div className="font-bold text-gray-900 text-lg">{drivetrain}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-4 mb-4"></div>
          {/* Enhanced Payment Calculator */}
          <div className="mt-6">
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
              {/* Calculator Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 rounded-full p-3">
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold"> Payment Calculator</h2>
                    <p className="text-indigo-100">Calculate your monthly payments</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Enhanced Calculator Tabs */}
                <div className="flex justify-center gap-3 mb-6">
                  {(packetTemplate === 'cash-only' ? ['cash'] : ['finance', 'cash']).map((type) => (
                    <button
                      key={type}
                      className={`px-8 py-3 rounded-2xl font-bold text-lg border-2 transition-all duration-200 shadow-lg focus:outline-none focus:ring-4 focus:ring-indigo-300 ${
                        paymentType === type 
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-indigo-600 shadow-xl scale-105' 
                          : 'bg-white text-indigo-600 border-indigo-300 hover:bg-indigo-50 hover:scale-105'
                      }`}
                      onClick={() => handlePaymentTypeChange(type)}
                    >
                      {type === 'finance' ? '💳 FINANCE' : '💵 CASH'}
                    </button>
                  ))}
                </div>
                {packetTemplate === 'cash-only' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 text-orange-800">
                      <span className="text-lg">💰</span>
                      <span className="font-semibold text-sm">Cash Purchase Only</span>
                    </div>
                    <p className="text-orange-700 text-xs mt-1">This vehicle is available for cash purchase only. Financing options are not available.</p>
                  </div>
                )}
                <div className="border-t border-gray-200 my-2" />
                {paymentType === 'cash' ? (
                  <div className="flex flex-col items-center py-4">
                    <div className="text-xl font-bold mb-2 text-gray-900">Estimated Cash Price</div>
                    <div className="text-4xl font-extrabold mb-2 text-gray-900">${(calcVehiclePrice + (includeTaxes ? calcTaxes : 0) + calcFees - netTradeInCredit).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-lg font-semibold text-gray-900">Include Taxes</span>
                      <label className="relative inline-flex items-center cursor-pointer w-12 h-7">
                        <input type="checkbox" className="sr-only peer" checked={includeTaxes} onChange={() => handleIncludeTaxesChange(v => !v)} />
                        <div className="w-12 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:bg-black transition"></div>
                        <div className="absolute left-1 top-1 bg-white w-5 h-5 rounded-full shadow peer-checked:translate-x-5 transition-transform"></div>
                      </label>
                    </div>
                    {/* Zip Code Input for Tax Rate - Cash Section */}
                    {includeTaxes && (
                      <div className="w-full max-w-xs mb-4">
                        <label className="block text-gray-700 font-semibold mb-1">Zip Code for Tax Rate</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            className="flex-1 py-2 border rounded-lg px-3"
                            value={zipCode}
                            placeholder="Enter zip code"
                            maxLength={5}
                            onChange={handleZipCodeChangeLogged}
                          />
                          {taxLoading && (
                            <div className="w-5 h-5 border-2 border-gray-300 border-t-black rounded-full animate-spin"></div>
                          )}
                        </div>
                        {zipCode.length === 5 && taxRate > 0 && (
                          <div className="text-xs text-gray-600 mt-1">
                            Tax Rate: {taxRate.toFixed(2)}% ({taxRegion}, {taxState})
                          </div>
                        )}
                        {zipCode.length === 5 && taxRate === 0 && !taxLoading && (
                          <div className="text-xs text-red-600 mt-1">
                            Tax rate not found for this zip code
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-4 mb-4 mt-2">
                      <span className="font-semibold text-base text-black">Trade-In Estimate</span>
                      <label className="relative inline-flex items-center cursor-pointer w-12 h-7">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={showTradeIn}
                          onChange={e => handleShowTradeInChange(e.target.checked)}
                        />
                        <div className="w-12 h-7 bg-gray-300 rounded-full peer peer-checked:bg-black transition-colors duration-200"></div>
                        <div className="absolute left-1 top-1 w-5 h-5 bg-white border border-gray-300 rounded-full transition-transform duration-200 peer-checked:translate-x-5"></div>
                      </label>
                    </div>
                    {showTradeIn && (
                      <div className="w-full bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
                        <label className="font-semibold text-blue-900">Estimated Trade-In Value</label>
                        <input
                          type="number"
                          className="border border-gray-300 rounded-lg px-3 py-2 w-full"
                          placeholder="Enter your vehicle's estimated trade-in value"
                          value={tradeInValue}
                          onChange={handleTradeInValueChange}
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="checkbox"
                            id="hasTradePayoff"
                            checked={hasTradePayoff}
                            onChange={e => handleHasTradePayoffChange(e.target.checked)}
                          />
                          <label htmlFor="hasTradePayoff" className="text-blue-900 font-medium">Do you have a loan payoff on your trade-in?</label>
                        </div>
                        {hasTradePayoff && (
                          <div>
                            <label className="font-semibold text-blue-900">Current Loan Payoff Amount</label>
                            <input
                              type="number"
                              className="border border-gray-300 rounded-lg px-3 py-2 w-full"
                              placeholder="Enter your current loan payoff amount"
                              value={tradePayoff}
                              onChange={handleTradePayoffChange}
                            />
                          </div>
                        )}
                        <a
                          href={dealership ? `https://wierscalculator.com/trade-in/${dealership.name.split(' ')[0].toLowerCase()}` : "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl text-center shadow transition"
                        >
                          What's My Trade Worth?
                        </a>
                      </div>
                    )}
                    <div className="bg-gray-100 rounded-xl p-4 mt-2 border border-gray-200 shadow-sm w-full max-w-xs">
                      <div className="flex justify-between items-center mb-1 text-xs">
                        <span className="text-gray-600">Vehicle Price:</span>
                        <span className="font-semibold text-gray-800">${calcVehiclePrice.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center mb-1 text-xs">
                        <span className="text-gray-600">Doc Fee:</span>
                        <span className="font-semibold text-gray-800">${calcFees.toLocaleString()}</span>
                      </div>
                      {includeTaxes && (
                        <div className="flex justify-between items-center mb-1 text-xs">
                          <span className="text-gray-600">Taxes:</span>
                          <span className="font-semibold text-gray-800">${calcTaxes.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        </div>
                      )}
                      {showTradeIn && netTradeInCredit > 0 && (
                        <div className="flex justify-between items-center mb-1 text-xs text-green-600">
                          <span className="text-gray-600">Trade-In Credit:</span>
                          <span className="font-semibold text-green-600">-${netTradeInCredit.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center mt-2 text-base">
                        <span className="text-gray-700 font-semibold">Total:</span>
                        <span className="text-xl font-bold text-black">${(calcVehiclePrice + (includeTaxes ? calcTaxes : 0) + calcFees - netTradeInCredit).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Payment & Down Payment */}
                    <div className="flex justify-between items-center mb-2 gap-4">
                      <div className="text-center flex-1">
                        <div className="text-gray-600 text-xs">Monthly Payment</div>
                        <div className="text-3xl font-bold text-black">${isFinite(calcMonthlyPayment) ? calcMonthlyPayment.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '--'}</div>
                      </div>
                      <div className="text-center flex-1">
                        <div className="text-gray-600 text-xs">Down Payment</div>
                        <input
                          type="number"
                          className="w-full py-3 border rounded-lg px-3"
                          placeholder="Enter amount"
                          value={calcDownPayment}
                          min={0}
                          max={calcVehiclePrice}
                          onChange={handleDownPaymentChange}
                        />
                      </div>
                    </div>
                    {/* Down Payment Slider */}
                    <input
                      type="range"
                      min={0}
                      max={calcVehiclePrice}
                      step={100}
                      value={calcDownPayment || 0}
                      onChange={handleDownPaymentChange}
                      className="w-full accent-black mb-2"
                    />
                    <div className="border-t border-gray-200 my-2" />
                    {/* Term Selection */}
                    <div className="mb-2">
                      <div className="text-gray-600 text-xs mb-1">Term</div>
                      <div className="flex gap-2 justify-center flex-wrap">
                        {[36, 48, 60, 72, 84].map((t) => (
                          <button
                            key={t}
                            className={`px-4 py-2 rounded-full border font-semibold transition-all duration-150 shadow-sm focus:outline-none focus:ring-2 focus:ring-black/30 ${calcTerm === t ? 'bg-black text-white border-black shadow' : 'bg-white text-black border-gray-300 hover:bg-gray-100'}`}
                            onClick={() => handleTermChange(t)}
                          >
                            {t} months
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Credit Score Slider */}
                    <div className="mb-2">
                      <div className="text-gray-600 text-xs mb-1">Your Credit</div>
                      <div className="flex items-center gap-2">
                        <div className="text-gray-500">Credit Score</div>
                        <div className="font-semibold">{calcCreditScore}</div>
                      </div>
                      <input type="range" min="500" max="850" step="10" value={calcCreditScore} onChange={handleCreditScoreChange} className="w-full" />
                    </div>
                    {/* Taxes Toggle */}
                    <div className="flex items-center justify-center gap-4 mb-4 mt-2">
                      <span className="font-semibold text-base text-black">Include Taxes</span>
                      <label className="relative inline-flex items-center cursor-pointer w-12 h-7">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={includeTaxes}
                          onChange={e => handleIncludeTaxesChange(e.target.checked)}
                        />
                        <div className="w-12 h-7 bg-gray-300 rounded-full peer peer-checked:bg-black transition-colors duration-200"></div>
                        <div className="absolute left-1 top-1 w-5 h-5 bg-white border border-gray-300 rounded-full transition-transform duration-200 peer-checked:translate-x-5"></div>
                      </label>
                    </div>
                    {/* Zip Code Input for Tax Rate */}
                    {includeTaxes && (
                      <div className="mb-4">
                        <div className="text-gray-600 text-xs mb-1">Zip Code for Tax Rate</div>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            className="flex-1 py-3 border rounded-lg px-3"
                            value={zipCode}
                            placeholder="Enter zip code"
                            maxLength={5}
                            onChange={handleZipCodeChangeLogged}
                          />
                          {taxLoading && (
                            <div className="w-5 h-5 border-2 border-gray-300 border-t-black rounded-full animate-spin"></div>
                          )}
                        </div>
                        {zipCode.length === 5 && taxRate > 0 && (
                          <div className="text-xs text-gray-600 mt-1">
                            Tax Rate: {taxRate.toFixed(2)}% ({taxRegion}, {taxState})
                          </div>
                        )}
                        {zipCode.length === 5 && taxRate === 0 && !taxLoading && (
                          <div className="text-xs text-red-600 mt-1">
                            Tax rate not found for this zip code
                          </div>
                        )}
                      </div>
                    )}
                    {/* Trade-In Toggle: Only show when paymentType is 'finance' */}
                    {paymentType === 'finance' && (
                      <div className="flex items-center justify-center gap-4 mb-4 mt-2">
                        <span className="font-semibold text-base text-black">Trade-In Estimate</span>
                        <label className="relative inline-flex items-center cursor-pointer w-12 h-7">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={showTradeIn}
                            onChange={e => handleShowTradeInChange(e.target.checked)}
                          />
                          <div className="w-12 h-7 bg-gray-300 rounded-full peer peer-checked:bg-black transition-colors duration-200"></div>
                          <div className="absolute left-1 top-1 w-5 h-5 bg-white border border-gray-300 rounded-full transition-transform duration-200 peer-checked:translate-x-5"></div>
                        </label>
                      </div>
                    )}
                    {paymentType === 'finance' && showTradeIn && (
                      <div className="w-full bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
                        <label className="font-semibold text-blue-900">Estimated Trade-In Value</label>
                        <input
                          type="number"
                          className="border border-gray-300 rounded-lg px-3 py-2 w-full"
                          placeholder="Enter your vehicle's estimated trade-in value"
                          value={tradeInValue}
                          onChange={handleTradeInValueChange}
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="checkbox"
                            id="hasTradePayoff"
                            checked={hasTradePayoff}
                            onChange={e => handleHasTradePayoffChange(e.target.checked)}
                          />
                          <label htmlFor="hasTradePayoff" className="text-blue-900 font-medium">Do you have a loan payoff on your trade-in?</label>
                        </div>
                        {hasTradePayoff && (
                          <div>
                            <label className="font-semibold text-blue-900">Current Loan Payoff Amount</label>
                            <input
                              type="number"
                              className="border border-gray-300 rounded-lg px-3 py-2 w-full"
                              placeholder="Enter your current loan payoff amount"
                              value={tradePayoff}
                              onChange={handleTradePayoffChange}
                            />
                          </div>
                        )}
                        <a
                          href={dealership ? `https://wierscalculator.com/trade-in/${dealership.name.split(' ')[0].toLowerCase()}` : "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl text-center shadow transition"
                        >
                          What's My Trade Worth?
                        </a>
                      </div>
                    )}
                    {/* APR Input */}
                    <div className="bg-gray-50 rounded-lg p-2 shadow">
                      <div className="flex justify-between items-center mb-1">
                        <div className="text-gray-500">APR</div>
                        <input
                          type="number"
                          value={calcApr}
                          onChange={handleAprChange}
                          className="font-semibold border rounded px-2 py-1 w-24 text-right"
                        />
                      </div>
                      <div className="text-xs text-gray-400 text-center">Suggested: {getAprForCreditScore(calcCreditScore, 'finance')}%</div>
                    </div>
                    {/* Payment Summary */}
                    <div className="bg-gray-100 rounded-xl p-4 mt-2 border border-gray-200 shadow-sm w-full max-w-xs">
                      <div className="flex justify-between items-center mb-1 text-xs">
                        <span className="text-gray-600">Term:</span>
                        <span className="font-semibold text-gray-800">{`${calcTerm} months`}</span>
                      </div>
                      {calcDownPayment && calcDownPayment > 0 && (
                      <div className="flex justify-between items-center mb-1 text-xs">
                        <span className="text-gray-600">Down Payment:</span>
                                                      <span className="font-semibold text-gray-800">${Number(calcDownPayment).toLocaleString()}</span>
                      </div>
                      )}
                      {showTradeIn && netTradeInCredit > 0 && (
                        <div className="flex justify-between items-center mb-1 text-xs text-green-600">
                          <span className="text-gray-600">Trade-In Credit:</span>
                          <span className="font-semibold text-green-600">-${netTradeInCredit.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center mb-1 text-xs">
                        <span className="text-gray-600">APR:</span>
                        <span className="font-semibold text-gray-800">{`${calcApr.toFixed(2)}%`}</span>
                      </div>
                      <div className="flex justify-between items-center mb-1 text-xs">
                        <span className="text-gray-600">Doc Fee:</span>
                        <span className="font-semibold text-gray-800">${calcFees.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      </div>
                      {includeTaxes && (
                        <div className="flex justify-between items-center mb-1 text-xs">
                          <span className="text-gray-600">Taxes:</span>
                          <span className="font-semibold text-gray-800">${calcTaxes.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center mt-2 text-base">
                        <span className="text-gray-700 font-semibold">Monthly Payment:</span>
                        <span className="text-xl font-bold text-black">${isFinite(calcMonthlyPayment) ? calcMonthlyPayment.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '--'}</span>
                      </div>
                    </div>
                  </> )}
              </div>
              {/* Calculator UI End */}
            </div>
          </div>
          {logId && (
            <div className="mt-6">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 rounded-full p-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>  
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">🚀 Next Step</h3>
                      <p className="text-orange-100 text-sm">Get your personalized payment quote</p>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <button
                    className="w-full bg-gradient-to-r from-green-500 to-blue-600 text-white px-8 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-200 text-lg flex items-center justify-center gap-3 hover:scale-105 disabled:opacity-50"
                    onClick={async () => {
                      await handleSubmitOptions();
                      if (submitStatus !== 'error') {
                        setRedirecting(true);
                        setTimeout(() => {
                          window.location.href = 'https://www.wiersauto.com/financing/application.htm';
                        }, 2000);
                      }
                    }}
                    disabled={submitStatus === 'submitting' || redirecting}
                  >
                    {submitStatus === 'submitting' ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Submitting...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-xl">🚀</span>
                        <span>What's My Exact Payment?</span>
                      </div>
                    )}
                  </button>
                </div>
              </div>
              {redirecting && (
                <div className="flex flex-col items-center mt-4">
                  <svg className="animate-spin h-8 w-8 text-blue-600 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                  <div className="text-blue-700 font-semibold text-lg">Redirecting to finance application…</div>
                </div>
              )}
              {submitStatus === 'success' && !redirecting && <div className="text-green-700 font-semibold mt-2">Options submitted!</div>}
              {submitStatus === 'error' && <div className="text-red-600 font-semibold mt-2">Failed to submit. Please try again.</div>}
            </div>
          )}
        </div>
      </div>
      {/* Inspection Forms Modal */}
      {showInspectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="pdf-modal bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-6xl p-0 sm:p-6 relative flex flex-col items-center overflow-y-auto max-h-screen sm:max-h-[90vh]">
            <button
              className="absolute top-2 right-2 text-black bg-white rounded-full text-4xl sm:text-2xl font-bold z-20 p-3 sm:p-1.5 shadow focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={{ minWidth: 52, minHeight: 52 }}
              onClick={() => setShowInspectionModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="font-bold text-gray-800 mb-2 sm:mb-4 text-base sm:text-lg">Inspection Forms</div>
            {inspectionPdfUrl ? (
              <>
                {/* Mobile: Better PDF experience */}
                <div className="block sm:hidden w-full px-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                    <div className="text-6xl mb-4"></div>
                    <div className="text-lg font-semibold text-blue-900 mb-3">Inspection Forms PDF</div>
                    <div className="text-sm text-gray-600 mb-6">
                      For the best mobile viewing experience, please open the PDF in a new tab or download it to your device.
                    </div>
                    <div className="space-y-3">
                      <a
                        href={inspectionPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full bg-blue-600 text-white font-semibold rounded-lg px-4 py-4 text-base shadow hover:bg-blue-700 transition"
                        onClick={() => trackInteraction('pdf_clicked', { type: 'inspection', action: 'open', platform: 'mobile', vin: vehicle.vin || vin })}
                      >
                         Open in New Tab
                      </a>
                      <a
                        href={inspectionPdfUrl}
                        download
                        className="block w-full bg-green-600 text-white font-semibold rounded-lg px-4 py-4 text-base shadow hover:bg-green-700 transition"
                        onClick={() => trackInteraction('pdf_clicked', { type: 'inspection', action: 'download', platform: 'mobile', vin: vehicle.vin || vin })}
                      >
                         Download PDF
                      </a>
                    </div>
                    <div className="text-xs text-gray-500 mt-4">
                      💡 Tip: After opening, you can zoom and scroll normally in your browser
                    </div>
                  </div>
                </div>
                
                {/* Desktop: Keep iframe */}
                <div className="hidden sm:block w-full">
                  <iframe
                    src={inspectionPdfUrl}
                    title="Inspection Forms PDF"
                    className="w-full h-[85vh] rounded-xl border shadow"
                    style={{ minHeight: '60vh' }}
                  />
                </div>
              </>
            ) : (
              <div className="w-full h-64 flex items-center justify-center bg-gray-100 rounded-xl text-gray-500 text-lg shadow-inner">
                No inspection PDF uploaded for this vehicle.
              </div>
            )}
          </div>
        </div>
      )}
      {/* AutoCheck History Modal */}
      {showAutoCheckModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="pdf-modal bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-6xl p-2 sm:p-6 relative flex flex-col items-center overflow-y-auto max-h-screen sm:max-h-[90vh]">
            <button
              className="absolute top-2 right-2 text-black bg-white rounded-full text-3xl sm:text-2xl font-bold z-20 p-2 sm:p-1.5 shadow focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={{ minWidth: 44, minHeight: 44 }}
              onClick={() => setShowAutoCheckModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="font-bold text-gray-800 mb-4 text-lg">AutoCheck History</div>
            {autoCheckPdfUrl ? (
              <>
                {/* Mobile: Better PDF experience */}
                <div className="block sm:hidden w-full px-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                    <div className="text-6xl mb-4"></div>
                    <div className="text-lg font-semibold text-blue-900 mb-3">AutoCheck History PDF</div>
                    <div className="text-sm text-gray-600 mb-6">
                      For the best mobile viewing experience, please open the PDF in a new tab or download it to your device.
                    </div>
                    <div className="space-y-3">
                      <a
                        href={autoCheckPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full bg-blue-600 text-white font-semibold rounded-lg px-4 py-4 text-base shadow hover:bg-blue-700 transition"
                        onClick={() => trackInteraction('pdf_clicked', { type: 'autocheck', action: 'open', platform: 'mobile', vin: vehicle.vin || vin })}
                      >
                         Open in New Tab
                      </a>
                      <a
                        href={autoCheckPdfUrl}
                        download
                        className="block w-full bg-green-600 text-white font-semibold rounded-lg px-4 py-4 text-base shadow hover:bg-green-700 transition"
                        onClick={() => trackInteraction('pdf_clicked', { type: 'autocheck', action: 'download', platform: 'mobile', vin: vehicle.vin || vin })}
                      >
                         Download PDF
                      </a>
                    </div>
                    <div className="text-xs text-gray-500 mt-4">
                      💡 Tip: After opening, you can zoom and scroll normally in your browser
                    </div>
                  </div>
                </div>
                
                {/* Desktop: Keep iframe */}
                <div className="hidden sm:block w-full">
                  <iframe
                    src={autoCheckPdfUrl}
                    title="AutoCheck PDF"
                    className="w-full h-[85vh] rounded-xl border shadow"
                    style={{ minHeight: '60vh' }}
                  />
                </div>
              </>
            ) : (
              <div className="w-full h-64 flex items-center justify-center bg-gray-100 rounded-xl text-gray-500 text-lg shadow-inner">
                No AutoCheck PDF uploaded for this vehicle.
              </div>
            )}
          </div>
        </div>
      )}
      {/* Original MSRP / Options Modal */}
      {showMsrpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="pdf-modal bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-6xl p-2 sm:p-6 relative flex flex-col items-center overflow-y-auto max-h-screen sm:max-h-[90vh]">
            <button
              className="absolute top-2 right-2 text-black bg-white rounded-full text-3xl sm:text-2xl font-bold z-20 p-2 sm:p-1.5 shadow focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={{ minWidth: 44, minHeight: 44 }}
              onClick={() => setShowMsrpModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="font-bold text-gray-800 mb-4 text-lg">Original MSRP / Options</div>
            {msrpPdfUrl ? (
              <>
                {/* Mobile: Better PDF experience */}
                <div className="block sm:hidden w-full px-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                    <div className="text-6xl mb-4"></div>
                    <div className="text-lg font-semibold text-blue-900 mb-3">Original MSRP / Options PDF</div>
                    <div className="text-sm text-gray-600 mb-6">
                      For the best mobile viewing experience, please open the PDF in a new tab or download it to your device.
                    </div>
                    <div className="space-y-3">
                      <a
                        href={msrpPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full bg-blue-600 text-white font-semibold rounded-lg px-4 py-4 text-base shadow hover:bg-blue-700 transition"
                        onClick={() => trackInteraction('pdf_clicked', { type: 'msrp', action: 'open', platform: 'mobile', vin: vehicle.vin || vin })}
                      >
                         Open in New Tab
                      </a>
                      <a
                        href={msrpPdfUrl}
                        download
                        className="block w-full bg-green-600 text-white font-semibold rounded-lg px-4 py-4 text-base shadow hover:bg-green-700 transition"
                        onClick={() => trackInteraction('pdf_clicked', { type: 'msrp', action: 'download', platform: 'mobile', vin: vehicle.vin || vin })}
                      >
                         Download PDF
                      </a>
                    </div>
                    <div className="text-xs text-gray-500 mt-4">
                      💡 Tip: After opening, you can zoom and scroll normally in your browser
                    </div>
                  </div>
                </div>
                
                {/* Desktop: Keep iframe */}
                <div className="hidden sm:block w-full">
                  <iframe
                    src={msrpPdfUrl}
                    title="MSRP PDF"
                    className="w-full h-[85vh] rounded-xl border shadow"
                    style={{ minHeight: '60vh' }}
                  />
                </div>
              </>
            ) : (
              <div className="w-full h-64 flex items-center justify-center bg-gray-100 rounded-xl text-gray-500 text-lg shadow-inner">
                No MSRP PDF uploaded for this vehicle.
              </div>
            )}
          </div>
        </div>
      )}
      {/* Photo Modal */}
      {photoModal.open && gallery[photoModal.index] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
          <div className="image-modal relative max-w-6xl w-full flex flex-col items-center">
            <button
              className="absolute top-2 right-2 text-black bg-white border border-black rounded-full text-4xl font-bold z-20 p-2 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={{ minWidth: 48, minHeight: 48 }}
              onClick={() => setPhotoModal({ open: false, index: 0 })}
              aria-label="Close"
            >
              &times;
            </button>
            {/* Pinch-to-zoom for mobile */}
            {window.innerWidth < 640 ? (
              <PinchZoomImage src={gallery[photoModal.index]} alt={`Vehicle Photo ${photoModal.index + 1}`} />
            ) : (
              <div className="flex items-center justify-center w-[900px] max-w-full h-[75vh] max-h-[85vh] bg-white rounded-xl shadow-lg">
                <img
                  src={gallery[photoModal.index]}
                  alt={`Vehicle Photo ${photoModal.index + 1}`}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            )}
            {gallery.length > 1 && (
              <div className="flex gap-4 mt-4 items-center justify-center w-[900px] max-w-full">
                <button
                  className="px-4 py-2 bg-white/80 rounded shadow text-lg font-bold"
                                      onClick={() => {
                      const newIndex = (photoModal.index - 1 + gallery.length) % gallery.length;
                      setPhotoModal({ open: true, index: newIndex });
                      trackInteraction('photo_clicked', { type: 'navigation', direction: 'prev', currentPhoto: newIndex + 1, totalPhotos: gallery.length, vin: vehicle.vin || vin });
                    }}
                >
                  &larr;
                </button>
                <span className="text-white font-semibold min-w-[70px] text-center">Photo {photoModal.index + 1} of {gallery.length}</span>
                <button
                  className="px-4 py-2 bg-white/80 rounded shadow text-lg font-bold"
                                      onClick={() => {
                      const newIndex = (photoModal.index + 1) % gallery.length;
                      setPhotoModal({ open: true, index: newIndex });
                      trackInteraction('photo_clicked', { type: 'navigation', direction: 'next', currentPhoto: newIndex + 1, totalPhotos: gallery.length, vin: vehicle.vin || vin });
                    }}
                >
                  &rarr;
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// Simple pinch-to-zoom and pan for mobile images
function PinchZoomImage({ src, alt }) {
  const imgRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const [state, setState] = React.useState({ scale: 1, lastScale: 1, x: 0, y: 0, lastX: 0, lastY: 0, touches: 0 });

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let startDist = 0, startScale = 1, startX = 0, startY = 0, lastX = 0, lastY = 0;
    let isPanning = false;
    function getDist(e) {
      const [a, b] = e.touches;
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }
    function onTouchStart(e) {
      if (e.touches.length === 2) {
        startDist = getDist(e);
        startScale = state.scale;
      } else if (e.touches.length === 1 && state.scale > 1) {
        isPanning = true;
        startX = e.touches[0].clientX - state.x;
        startY = e.touches[0].clientY - state.y;
      }
      setState(s => ({ ...s, touches: e.touches.length }));
    }
    function onTouchMove(e) {
      if (e.touches.length === 2) {
        const dist = getDist(e);
        let newScale = Math.max(1, Math.min(4, startScale * (dist / startDist)));
        setState(s => ({ ...s, scale: newScale, lastScale: newScale }));
      } else if (e.touches.length === 1 && isPanning && state.scale > 1) {
        let x = e.touches[0].clientX - startX;
        let y = e.touches[0].clientY - startY;
        setState(s => ({ ...s, x, y, lastX: x, lastY: y }));
      }
      e.preventDefault();
    }
    function onTouchEnd(e) {
      isPanning = false;
      setState(s => ({ ...s, touches: e.touches.length }));
    }
    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);
    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
    };
  }, [state.scale, state.x, state.y]);
  return (
    <div
      ref={containerRef}
      style={{
        touchAction: 'none',
        width: '100vw',
        height: '75vh',
        maxWidth: '100vw',
        maxHeight: '85vh',
        overflow: 'hidden',
        background: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        style={{
          transform: `scale(${state.scale}) translate(${state.x / state.scale}px, ${state.y / state.scale}px)`,
          transition: state.touches === 0 ? 'transform 0.2s' : 'none',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          userSelect: 'none',
          touchAction: 'none',
        }}
        draggable={false}
      />
    </div>
  );
}